// Sale undo (IMPLEMENTATION §0.2 R13). The sim keeps `transient.lastSale` until the next sale replaces it
// (no tick expiry; the UI shows the toast for 5 s). Undo needs the token of that sale, cash ≥ its value
// (cash never clamps: otherwise "spent") and room: every item goes to its origin cell, else the nearest free
// cell by BFS, items placed in ascending id order (id-stable); a Crate "Sell all" needs its Crate (same or
// no family, room for the counts). Otherwise "noRoom". Build undo lives in buildOps.ts.
import type { RejectReason } from '../commands.ts';
import { cellIndex } from '../core/grid.ts';
import type { Cell, Int } from '../core/types.ts';
import type { SimDefs } from '../defs.ts';
import { findBuilding } from '../entities.ts';
import { nearestFree, type Occ } from '../occupancy.ts';
import type { SimState } from '../state.ts';
import { crateTotal } from './accept.ts';

export type UndoSellPlan = { ok: true; cells: Cell[] } | { ok: false; reason: RejectReason };

/** Pure: where each item of the last sale would go, or why the undo is refused. */
export function planUndoSell(s: SimState, d: SimDefs, occ: Occ, token: Int): UndoSellPlan {
  const sale = s.transient.lastSale;
  if (sale === null || sale.token !== token) return { ok: false, reason: 'stale' };
  if (s.wallet.cash < sale.value) return { ok: false, reason: 'spent' };
  if (sale.crate !== null) {
    const b = sale.bid === null ? null : findBuilding(s, sale.bid);
    if (b === null || b.type !== 'crate' || b.ghost !== null)
      return { ok: false, reason: 'noRoom' };
    if (b.fam !== null && b.fam !== sale.crate.fam) return { ok: false, reason: 'noRoom' };
    let n = 0;
    for (const k of sale.crate.counts) n += k;
    const cap = d.buildings.crate.capacity[b.level - 1] ?? 0;
    if (crateTotal(b) + n > cap) return { ok: false, reason: 'noRoom' };
  }
  const claimed: boolean[] = [];
  const cells: Cell[] = [];
  for (const it of sale.items) {
    const at = nearestFree(occ, it.x, it.y, claimed);
    if (at === null) return { ok: false, reason: 'noRoom' };
    claimed[cellIndex(occ.cols, at.x, at.y)] = true;
    cells.push(at);
  }
  return { ok: true, cells };
}
